import React, { useState, useEffect } from 'react';
import { 
  Card, Form, Input, InputNumber, Switch, Button, 
  Space, message, Divider, Tag, Tooltip, Spin, Upload, Alert, Tabs
} from 'antd';
import {
  SaveOutlined, ReloadOutlined, SettingOutlined,
  InfoCircleOutlined, CheckCircleOutlined, SyncOutlined,
  CloudUploadOutlined, PictureOutlined, GlobalOutlined,
  DollarOutlined, SafetyCertificateOutlined, VideoCameraOutlined
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { apiClient } from '@/services/api';

const { TextArea } = Input;

interface SystemConfigEditorProps {
  onSave?: () => void;
}

const SystemConfigEditor: React.FC<SystemConfigEditorProps> = ({ onSave }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [saved, setSaved] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // 加载配置
  const loadConfig = async () => {
    try {
      setLoadingConfig(true);
      
      // 加载站点配置
      const siteResponse = await apiClient.get('/admin/site-config');
      
      // 加载积分配置
      const pointsKeys = [
        'register_bonus_points',
        'video_cost_basic',
        'video_cost_pro',
        'video_cost_premium',
        'image_cost_basic',
        'image_cost_additional',
        'image_cost_watermark'
      ];
      
      const pointsConfig = {};
      for (const key of pointsKeys) {
        try {
          const configResponse = await apiClient.get(`/admin/config/${key}`);
          if (configResponse.success && configResponse.data?.value) {
            pointsConfig[key] = parseFloat(configResponse.data.value) || configResponse.data.value;
          }
        } catch (e) {
          console.warn(`加载配置 ${key} 失败:`, e);
        }
      }
      
      if (siteResponse.success && siteResponse.data) {
        // 合并站点配置和积分配置
        const allConfig = {
          ...siteResponse.data,
          ...pointsConfig
        };
        
        // 设置表单值
        form.setFieldsValue(allConfig);
        
        // 设置Logo
        if (allConfig.siteLogo) {
          setLogoUrl(allConfig.siteLogo);
          setFileList([{
            uid: '-1',
            name: 'logo.png',
            status: 'done',
            url: allConfig.siteLogo,
          }]);
        }
      }
    } catch (error: any) {
      console.error('加载配置失败:', error);
      message.error('加载配置失败: ' + (error.message || '未知错误'));
    } finally {
      setLoadingConfig(false);
    }
  };

  // 获取默认配置
  const getDefaultConfig = () => ({
    // 站点配置
    siteName: 'Sora2 AI Platform',
    siteDescription: '让创意触手可及，让想象成为现实',
    siteLogo: '',
    siteKeywords: 'AI,视频生成,图像生成,Sora2',
    siteFooter: '© 2024 Sora2 AI Platform. All rights reserved.',
    contactEmail: 'contact@sora2.ai',
    // 系统配置
    maxFileSize: 10,
    registerEnabled: true,
    videoGenEnabled: true,
    imageGenEnabled: true,
    paymentEnabled: true,
    rateLimitWindow: 15,
    rateLimitMax: 100,
    dailyGenLimit: 50,
    maxPromptLength: 2000,
    concurrentLimit: 5,
    videoCostBasic: 10,
    videoCostPro: 20,
    videoCostAdvanced: 30,
    imageCostBase: 10,
    imageCostExtra: 5,
    imageCostWatermark: 5,
  });

  // 组件加载时获取配置
  useEffect(() => {
    // 延迟加载，确保 Form 组件已经完全挂载
    const timer = setTimeout(() => {
    loadConfig();
    }, 0);
    return () => clearTimeout(timer);
  }, [form]);

  // 保存配置
  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      
      // 分离积分配置和站点配置
      const { 
        register_bonus_points, 
        video_cost_basic, 
        video_cost_pro, 
        video_cost_premium,
        image_cost_basic,
        image_cost_additional,
        image_cost_watermark,
        ...siteConfig 
      } = values;
      
      // 准备积分配置
      const pointsConfig = {
        register_bonus_points,
        video_cost_basic,
        video_cost_pro,
        video_cost_premium,
        image_cost_basic,
        image_cost_additional,
        image_cost_watermark
      };
      
      // 保存积分配置到 systemConfig
      if (Object.values(pointsConfig).some(v => v !== undefined && v !== null)) {
        for (const [key, value] of Object.entries(pointsConfig)) {
          if (value !== undefined && value !== null) {
            await apiClient.post('/admin/config', {
              key,
              value: String(value)
            });
          }
        }
      }
      
      // 添加Logo URL并保存站点配置
      const configData = {
        ...siteConfig,
        siteLogo: logoUrl
      };
      
      // 调用API保存站点配置
      const response = await apiClient.post('/admin/site-config', configData);
      
      if (response.success) {
        message.success('配置保存成功，已立即生效！首页将自动更新。');
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        
        // 重新加载配置以确保前端显示最新值
        await loadConfig();
        
        // 手动触发全局配置更新（确保所有页面都能收到）
        const { useSiteConfigStore } = await import('@/store/siteConfigStore');
        await useSiteConfigStore.getState().load();
        
        if (onSave) onSave();
      } else {
        throw new Error(response.message || '保存失败');
      }
    } catch (error: any) {
      console.error('保存配置失败:', error);
      message.error('保存失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 重置为默认配置
  const handleReset = () => {
    // 使用前端默认值重置表单
    form.setFieldsValue(getDefaultConfig());
    setLogoUrl('');
    setFileList([]);
    message.info('已重置为默认值（点击保存后生效）');
  };

  // 上传Logo配置
  const uploadProps: UploadProps = {
    name: 'file',
    listType: 'picture-card',
    fileList: fileList,
    maxCount: 1,
    accept: 'image/*',
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件！');
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('图片大小不能超过2MB！');
        return false;
      }
      return true;
    },
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        console.log('🔄 开始上传Logo到云存储...');
        
        const formData = new FormData();
        formData.append('file', file as File);
        formData.append('type', 'logo');

        const response = await apiClient.post('/admin/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        console.log('📡 上传响应:', response);

        if (response.success && response.data?.url) {
          setLogoUrl(response.data.url);
          // 用云端URL刷新上传列表，确保只显示一张正确预览
          setFileList([{
            uid: String(Date.now()),
            name: (file as File)?.name || 'logo.png',
            status: 'done',
            url: response.data.url,
          }]);
          console.log('✅ Logo上传成功，URL:', response.data.url);
          message.success(`Logo已上传到云存储！URL: ${response.data.url.substring(0, 50)}...`);
          onSuccess?.(response.data);
        } else {
          console.error('❌ 上传失败:', response.message);
          message.error(response.message || '上传失败');
          onError?.(new Error(response.message || '上传失败'));
        }
      } catch (error: any) {
        console.error('❌ 上传异常:', error);
        const errorMsg = error.response?.data?.message || error.message || '上传失败';
        message.error('上传失败: ' + errorMsg);
        onError?.(error);
      }
    },
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    onRemove: () => {
      setLogoUrl('');
      setFileList([]);
    },
  };

  // 如果正在加载配置，显示加载状态
  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center py-20">
        <Space direction="vertical" align="center" size="large">
          <Spin size="large" />
          <div className="text-gray-600">正在加载系统配置...</div>
        </Space>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={getDefaultConfig()}
      >
        <Tabs
          defaultActiveKey="site"
          items={[
            {
              key: 'site',
              label: '站点配置',
              children: (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 站点配置 */}
          <Card 
            title={
              <span>
                <GlobalOutlined className="mr-2 text-blue-600" />
                站点配置
              </span>
            }
            className="shadow-md"
          >
            <Form.Item
              label="站点名称"
              name="siteName"
              rules={[{ required: true, message: '请输入站点名称' }]}
              extra="显示在首页标题和Header"
            >
              <Input placeholder="Sora2 AI Platform" maxLength={50} showCount />
            </Form.Item>

            <Form.Item
              label="站点描述"
              name="siteDescription"
              extra="显示在首页副标题"
            >
              <TextArea rows={2} placeholder="让创意触手可及，让想象成为现实" maxLength={100} showCount />
            </Form.Item>

            <Form.Item
              label="站点Logo"
              extra="推荐尺寸200x50px，最大2MB"
            >
              <Upload {...uploadProps}>
                {fileList.length === 0 && (
                  <div>
                    <CloudUploadOutlined className="text-2xl text-gray-400" />
                    <div className="mt-2 text-sm">上传Logo</div>
                  </div>
                )}
              </Upload>
            </Form.Item>

            <Form.Item
              label="站点关键词"
              name="siteKeywords"
              extra="用于SEO，多个关键词用逗号分隔"
            >
              <Input placeholder="AI,视频生成,图像生成,Sora2" />
            </Form.Item>

            <Form.Item
              label="联系邮箱"
              name="contactEmail"
              rules={[{ type: 'email', message: '请输入有效的邮箱' }]}
            >
              <Input placeholder="contact@sora2.ai" />
            </Form.Item>

            <Form.Item
              label="页脚文字"
              name="siteFooter"
            >
              <Input placeholder="© 2024 Sora2 AI Platform. All rights reserved." />
            </Form.Item>
                  </Card>

                  {/* 系统基本配置 */}
          <Card 
            title={
              <span>
                <SettingOutlined className="mr-2 text-green-600" />
                系统配置
              </span>
            }
            className="shadow-md"
          >
            <Form.Item
              label={
                <span>
                  最大文件大小
                  <Tooltip title="上传文件的最大限制">
                    <InfoCircleOutlined className="ml-2 text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="maxFileSize"
            >
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber min={1} max={100} style={{ width: '100%' }} placeholder="10" />
                <Button disabled style={{ width: '60px' }}>MB</Button>
              </Space.Compact>
            </Form.Item>

            <Divider plain>功能开关</Divider>

            <Form.Item
              label="用户注册"
              name="registerEnabled"
              valuePropName="checked"
            >
              <Switch checkedChildren="开启" unCheckedChildren="关闭" />
            </Form.Item>

            <Form.Item
              label="视频生成"
              name="videoGenEnabled"
              valuePropName="checked"
            >
              <Switch checkedChildren="开启" unCheckedChildren="关闭" />
            </Form.Item>

            <Form.Item
              label="图像生成"
              name="imageGenEnabled"
              valuePropName="checked"
            >
              <Switch checkedChildren="开启" unCheckedChildren="关闭" />
            </Form.Item>

            <Form.Item
              label="支付功能"
              name="paymentEnabled"
              valuePropName="checked"
            >
              <Switch checkedChildren="开启" unCheckedChildren="关闭" />
            </Form.Item>
                  </Card>
                </div>
              ),
            },

            {
              key: 'security',
              label: '安全限制',
              children: (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 安全限制配置 */}
          <Card 
            title={
              <span>
                <SafetyCertificateOutlined className="mr-2 text-red-600" />
                安全限制
              </span>
            }
            className="shadow-md"
          >
            <Alert
              message="安全配置说明"
              description="合理的限制可以防止滥用和保护系统稳定性"
              type="info"
              showIcon
              closable
              className="mb-4"
            />

            <Form.Item
              label={
                <span>
                  速率限制窗口
                  <Tooltip title="在指定时间窗口内限制请求次数">
                    <InfoCircleOutlined className="ml-2 text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="rateLimitWindow"
              extra="推荐: 15分钟"
            >
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber min={1} max={60} style={{ width: '100%' }} placeholder="15" />
                <Button disabled style={{ width: '60px' }}>分钟</Button>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              label={
                <span>
                  速率限制次数
                  <Tooltip title="在时间窗口内允许的最大请求次数">
                    <InfoCircleOutlined className="ml-2 text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="rateLimitMax"
              extra="推荐: 100次"
            >
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber min={10} max={1000} style={{ width: '100%' }} placeholder="100" />
                <Button disabled style={{ width: '60px' }}>次</Button>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              label={
                <span>
                  每日生成限制
                  <Tooltip title="每个用户每天最多生成次数">
                    <InfoCircleOutlined className="ml-2 text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="dailyGenLimit"
              extra="推荐: 50次"
            >
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber min={10} max={500} style={{ width: '100%' }} placeholder="50" />
                <Button disabled style={{ width: '70px' }}>次/天</Button>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              label={
                <span>
                  提示词最大长度
                  <Tooltip title="用户输入提示词的最大字符数">
                    <InfoCircleOutlined className="ml-2 text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="maxPromptLength"
              extra="推荐: 2000字符"
            >
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber min={500} max={5000} style={{ width: '100%' }} placeholder="2000" />
                <Button disabled style={{ width: '60px' }}>字符</Button>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              label={
                <span>
                  并发生成限制
                  <Tooltip title="同时进行的生成任务数量限制">
                    <InfoCircleOutlined className="ml-2 text-gray-400" />
                  </Tooltip>
                </span>
              }
              name="concurrentLimit"
              extra="推荐: 5个"
            >
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber min={1} max={20} style={{ width: '100%' }} placeholder="5" />
                <Button disabled style={{ width: '60px' }}>个</Button>
              </Space.Compact>
            </Form.Item>
                  </Card>
                </div>
              ),
            },
            {
              key: 'points',
              label: '积分配置',
              children: (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 注册赠送积分配置 */}
                  <Card 
                    title={
                      <span>
                        <DollarOutlined className="mr-2 text-green-600" />
                        注册赠送积分
                      </span>
                    }
                    className="shadow-md"
                  >
                    <Form.Item
                      label="新用户注册赠送积分"
                      name="register_bonus_points"
                      rules={[{ required: true, message: '请输入注册赠送积分' }]}
                      extra="新用户注册时自动获得的积分数量"
                    >
                      <Space.Compact style={{ width: '100%' }}>
                      <InputNumber 
                        min={0} 
                        max={10000} 
                        style={{ width: '100%' }} 
                        placeholder="10" 
                      />
                        <Button disabled style={{ width: '60px' }}>积分</Button>
                      </Space.Compact>
                    </Form.Item>
                    
                    <Alert
                      message="配置说明"
                      description="修改此配置后，新注册的用户将获得相应数量的积分。现有用户的积分不受影响。"
                      type="info"
                      showIcon
                    />
                  </Card>

                  {/* 视频生成费用配置 */}
                  <Card 
                    title={
                      <span>
                        <VideoCameraOutlined className="mr-2 text-blue-600" />
                        视频生成费用
                      </span>
                    }
                    className="shadow-md"
                  >
                    <Form.Item
                      label="基础版视频费用 (SORA2)"
                      name="video_cost_basic"
                      rules={[{ required: true, message: '请输入基础版视频费用' }]}
                      extra="基础版视频生成消耗的积分，对应SORA2模型"
                    >
                      <Space.Compact style={{ width: '100%' }}>
                      <InputNumber 
                        min={1} 
                        max={1000} 
                        style={{ width: '100%' }} 
                        placeholder="20" 
                      />
                        <Button disabled style={{ width: '60px' }}>积分</Button>
                      </Space.Compact>
                    </Form.Item>

                    <Form.Item
                      label="Pro版视频费用 (SORA2_PRO)"
                      name="video_cost_pro"
                      rules={[{ required: true, message: '请输入Pro版视频费用' }]}
                      extra="Pro版视频生成消耗的积分，对应SORA2_PRO模型"
                    >
                      <Space.Compact style={{ width: '100%' }}>
                      <InputNumber 
                        min={1} 
                        max={1000} 
                        style={{ width: '100%' }} 
                        placeholder="30" 
                      />
                        <Button disabled style={{ width: '60px' }}>积分</Button>
                      </Space.Compact>
                    </Form.Item>

                    <Form.Item
                      label="旗舰版视频费用 (SORA2_ADVANCED)"
                      name="video_cost_premium"
                      rules={[{ required: true, message: '请输入旗舰版视频费用' }]}
                      extra="旗舰版视频生成消耗的积分，对应SORA2_ADVANCED模型"
                    >
                      <Space.Compact style={{ width: '100%' }}>
                      <InputNumber 
                        min={1} 
                        max={1000} 
                        style={{ width: '100%' }} 
                        placeholder="40" 
                      />
                        <Button disabled style={{ width: '60px' }}>积分</Button>
                      </Space.Compact>
                    </Form.Item>
                  </Card>

                  {/* 图像生成费用配置 */}
                  <Card 
                    title={
                      <span>
                        <PictureOutlined className="mr-2 text-purple-600" />
                        图像生成费用
                      </span>
                    }
                    className="shadow-md"
                  >
                    <Form.Item
                      label="基础图像费用"
                      name="image_cost_basic"
                      rules={[{ required: true, message: '请输入基础图像费用' }]}
                      extra="生成一张图像消耗的积分"
                    >
                      <Space.Compact style={{ width: '100%' }}>
                      <InputNumber 
                        min={1} 
                        max={1000} 
                        style={{ width: '100%' }} 
                        placeholder="10" 
                      />
                        <Button disabled style={{ width: '60px' }}>积分</Button>
                      </Space.Compact>
                    </Form.Item>

                    <Form.Item
                      label="每增加一张图像费用"
                      name="image_cost_additional"
                      rules={[{ required: true, message: '请输入每增加一张图像费用' }]}
                      extra="批量生成时，每增加一张图像消耗的积分"
                    >
                      <Space.Compact style={{ width: '100%' }}>
                      <InputNumber 
                        min={1} 
                        max={1000} 
                        style={{ width: '100%' }} 
                        placeholder="5" 
                      />
                        <Button disabled style={{ width: '60px' }}>积分</Button>
                      </Space.Compact>
                    </Form.Item>

                    <Form.Item
                      label="去水印费用"
                      name="image_cost_watermark"
                      rules={[{ required: true, message: '请输入去水印费用' }]}
                      extra="去除图像水印消耗的积分"
                    >
                      <Space.Compact style={{ width: '100%' }}>
                      <InputNumber 
                        min={0} 
                        max={1000} 
                        style={{ width: '100%' }} 
                        placeholder="5" 
                      />
                        <Button disabled style={{ width: '60px' }}>积分</Button>
                      </Space.Compact>
                    </Form.Item>
                  </Card>

                  {/* 积分费用说明 */}
                  <Card 
                    title={
                      <span>
                        <InfoCircleOutlined className="mr-2 text-orange-600" />
                        积分费用说明
                      </span>
                    }
                    className="shadow-md lg:col-span-2"
                  >
                    <Alert
                      message="积分费用配置说明"
                      description={
                        <div className="space-y-2">
                          <p><strong>视频生成费用：</strong></p>
                          <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>基础版：适合简单视频生成，费用较低</li>
                            <li>Pro版：提供更高质量和更多功能</li>
                            <li>旗舰版：最高质量，支持复杂场景</li>
                          </ul>
                          <p><strong>图像生成费用：</strong></p>
                          <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>基础费用：生成第一张图像的费用</li>
                            <li>增加费用：批量生成时每增加一张的费用</li>
                            <li>去水印：去除图像水印的额外费用</li>
                          </ul>
                          <p><strong>注意：</strong>修改费用配置后，新生成的视频/图像将按新费用扣除积分。</p>
                        </div>
                      }
                      type="info"
                      showIcon
                    />
                  </Card>
                </div>
              ),
            },
          ]}
        />

        {/* 固定操作栏 */}
        <div className="sticky bottom-0 z-10 bg-white/80 backdrop-blur border-t mt-4">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 py-3 px-2">
          <Button 
            onClick={() => loadConfig()} 
            icon={<SyncOutlined />}
            size="large"
            className="w-full sm:w-auto sm:px-8"
            disabled={loading}
          >
            刷新配置
          </Button>
          <Button 
            onClick={handleReset} 
            icon={<ReloadOutlined />}
            size="large"
            className="w-full sm:w-auto sm:px-8"
            disabled={loading}
          >
            重置默认
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            icon={saved ? <CheckCircleOutlined /> : <SaveOutlined />}
            size="large"
            className="w-full sm:w-auto sm:px-8"
          >
            {saved ? '✅ 已保存' : '💾 保存配置'}
          </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default SystemConfigEditor;

